import { Dispatch, SetStateAction, useEffect, useRef, useState } from "react";
import { readSessionFilter, writeSessionFilter } from "../util/sessionFilters";

/**
 * useState kept in sessionStorage for the signed-in user, so a filter survives
 * leaving the page (pages unmount on tab switch) and coming back. Without a
 * key it is plain useState. Values must be JSON-safe; `serialize` trims what
 * is stored, `revive` rebuilds anything that is not JSON (dates).
 */
export function useSessionState<T>(
	key: string | null | undefined,
	initial: T | (() => T),
	revive?: (stored: any) => T | undefined,
	serialize?: (value: T) => unknown,
): [T, Dispatch<SetStateAction<T>>] {
	const [value, setValue] = useState<T>(() => {
		const stored = readSessionFilter<any>(key);
		if (stored !== undefined) {
			const revived = revive ? revive(stored) : (stored as T);
			if (revived !== undefined) return revived;
		}
		return typeof initial === "function" ? (initial as () => T)() : initial;
	});

	const firstRef = useRef(true);
	useEffect(() => {
		// The first render only echoes what was read (or the default).
		if (firstRef.current) {
			firstRef.current = false;
			return;
		}
		writeSessionFilter(key, serialize ? serialize(value) : value);
		// eslint-disable-next-line react-hooks/exhaustive-deps -- serialize is a pure formatter
	}, [key, value]);

	return [value, setValue];
}
